import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import i18n from 'i18n';
import { get, isEmpty, max, size } from 'lodash';
import getLocalized from 'utils/get-localized';
import scaleSize from 'utils/scale-size';
import { fetchEpisodes } from 'actions/details';
import List from 'components/List';
import withKeepingLastFocus from 'components/HOCs/withKeepingLastFocus';
import {
  SeasonsWrapper,
  SeasonsTabs,
  SeasonsTab,
  StyledEmptySeasonStub,
  StyledEmptySeasonStubTitle,
} from './StyledComponents';

const CONFIG = {
  enterTo: 'default-element',
  defaultElement: '.selectedTab',
};
const LIST_CONFIG = {
  leaveFor: {
    right: '',
  },
};

const TAB_ITEM_SIZES = [
  // small
  { range: [0, 8], size: 170 },
  // medium
  { range: [9, 16], size: 340 },
];

const isInRange = (v, r) => r[0] <= v && v <= r[1];

const EmptySeasonStub = ({ isLoading }) => {
  return (
    <StyledEmptySeasonStub>
      <StyledEmptySeasonStubTitle>
        {isLoading ? '' : i18n.t('detailsVOD.noEpisodesInSeason')}
      </StyledEmptySeasonStubTitle>
    </StyledEmptySeasonStub>
  );
};

const Seasons = (props) => {
  const dispatch = useDispatch();
  const { sectionName, contentId, seasons, isLoading } = props;
  const { onFocus, onListItemEnter, lastFocusKeeper } = props;
  // This is inner "last-focus" key. It's unique by vod item id
  const LAST_FOCUS_KEY = `DETAILS_SEASONS_${contentId}`;

  const [selectedSeasonIndex, setSelectedSeasonIndex] = useState(() => {
    return get(lastFocusKeeper.getSaved(LAST_FOCUS_KEY), ['index'], 0);
  });

  const isSelectedSeasonHasEpisodes = !isEmpty(get(seasons[selectedSeasonIndex], ['episodes', 'content']));

  const onTabEnter = useCallback(
    (_item, index) => {
      setSelectedSeasonIndex(index);
      lastFocusKeeper.save(LAST_FOCUS_KEY, { index });
    },
    [LAST_FOCUS_KEY, lastFocusKeeper],
  );

  const tabItemWidth = useMemo(() => {
    const titles = seasons.map((item) => size(getLocalized(item.titles, item.title).title));
    const maxLength = max(titles);
    // small by default
    let width = TAB_ITEM_SIZES[0].size;

    for (const { range, size } of TAB_ITEM_SIZES) if (isInRange(maxLength, range)) width = size;

    return width;
  }, [seasons]);

  const tabs = useMemo(() => {
    return (
      <SeasonsTabs
        sectionName={sectionName}
        config={CONFIG}
        listId="tabs-list"
        itemSize={scaleSize(tabItemWidth)}
        startScrollIndex={3}
        items={seasons}
        onFocus={onFocus}
        onEnter={onTabEnter}
        itemRender={({ item, index, ...rest }) => {
          const isSelected = index === selectedSeasonIndex;
          return (
            <SeasonsTab
              isSelected={isSelected}
              className={isSelected && 'selectedTab'}
              payload={index}
              index={index}
              itemWidth={scaleSize(tabItemWidth)}
              {...rest}
            >
              {getLocalized(item.titles, get(item, 'titles.UA.title', item.title))}
            </SeasonsTab>
          );
        }}
      />
    );
  }, [onFocus, onTabEnter, seasons, sectionName, selectedSeasonIndex, tabItemWidth]);

  const content = useMemo(() => {
    if (isSelectedSeasonHasEpisodes) {
      return (
        <List
          config={LIST_CONFIG}
          onListItemFocus={onFocus}
          onListItemEnter={onListItemEnter}
          listId={'season-episodes'}
          items={seasons[selectedSeasonIndex].episodes.content}
          itemType={'EPISODE'}
          listType={'LARGE'}
          extraPadding={0}
        />
      );
    }

    return <EmptySeasonStub isLoading={isLoading} />;
  }, [isSelectedSeasonHasEpisodes, isLoading, onFocus, onListItemEnter, seasons, selectedSeasonIndex]);

  useEffect(() => {
    const selectedSeason = seasons[selectedSeasonIndex];
    if (isEmpty(selectedSeason.episodes)) {
      dispatch(fetchEpisodes(selectedSeason.vodAssetsUrl, contentId, selectedSeasonIndex));
    }
  }, [seasons, contentId, selectedSeasonIndex, dispatch]);

  return (
    <SeasonsWrapper>
      {tabs}
      {content}
    </SeasonsWrapper>
  );
};

const seasonPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  titles: PropTypes.object.isRequired,
  title: PropTypes.object,
  episodes: PropTypes.shape({ content: PropTypes.array.isRequired }),
});

Seasons.propTypes = {
  sectionName: PropTypes.string.isRequired,
  contentId: PropTypes.number.isRequired,
  seasons: PropTypes.arrayOf(seasonPropType).isRequired,
  isLoading: PropTypes.bool,
  onFocus: PropTypes.func.isRequired,
  onListItemEnter: PropTypes.func.isRequired,
  lastFocusKeeper: PropTypes.object.isRequired,
};

export default withKeepingLastFocus(Seasons);
